import {
    CertificateTargetType,
    RequestCertificateInput,
} from '../types';

// FNP-1193 — the request form's values and its rules.
//
// ⚠️ THE VALIDATION IS A PURE FUNCTION, called directly by the tests.
//
// Not Formik: the rules here are cross-field (the region rule depends entirely
// on the chosen target) and the additional-names list needs per-row errors
// keyed by index. Expressing that through Formik's field-name machinery is more
// indirection than the rules themselves. Same conclusion the records module's
// form reached.

export type CertificateFormValues = {
    connectionId: string,
    domainName: string,
    // Additional names on the same certificate. This is how a tenant gets a
    // certificate for a hostname two levels down, which a parent's wildcard
    // does not cover.
    subjectAlternativeNames: string[],
    targetType: CertificateTargetType,
    region: string,
}

// ⚠️ THE REGION CLOUDFRONT REQUIRES, AND ONLY CLOUDFRONT.
//
// This is not a platform default and must never be applied to another target.
// A LOAD_BALANCER or API_GATEWAY certificate must live in the SAME region as
// the resource it serves, which the caller names.
export const CLOUDFRONT_REGION = 'us-east-1';

// The regions offered for a regional target. Not exhaustive — a tenant serving
// from somewhere else types it — but these cover the common cases without
// making the field a free-text guess for everyone.
export const COMMON_REGIONS = [
    'us-east-1',
    'us-east-2',
    'us-west-1',
    'us-west-2',
    'eu-west-1',
    'eu-west-2',
    'eu-central-1',
    'ap-south-1',
    'ap-southeast-1',
    'ap-southeast-2',
    'ap-northeast-1',
    'ca-central-1',
    'sa-east-1',
];

export const emptyCertificateFormValues = (
    connectionId?: string,
): CertificateFormValues => ({
    connectionId: connectionId ?? '',
    domainName: '',
    subjectAlternativeNames: [],
    // ⚠️ THE DEFAULT IS `UNSPECIFIED`, NOT `CLOUDFRONT`.
    //
    // Defaulting to CloudFront would rebuild, in the form, exactly the premise
    // the owner rejected: that certificates are a CloudFront feature. A tenant
    // managing a certificate without telling Flexi what consumes it is a real
    // and supported case, and it is the honest default for a field the user has
    // not touched.
    targetType: 'UNSPECIFIED',
    region: '',
});

/**
 * Whether a string is a plausible hostname for a certificate.
 *
 * Deliberately permissive: the provider is the authority on what it will
 * accept, and a client-side rule that is stricter than the provider's rejects
 * names that would have worked. This catches the typos that are certainly
 * wrong — empty, spaces, a leading or trailing dot, no dot at all — and lets
 * everything else through to be judged where the authority lives.
 *
 * ⚠️ A LEADING `*.` IS VALID and is the whole point of a wildcard certificate.
 */
export const isPlausibleHostname = (value: string): boolean => {

    const name = value.trim();

    if (name === '') {
        return false;
    }

    if (/\s/.test(name)) {
        return false;
    }

    // A wildcard is legal only as the FIRST label. `a.*.example.com` is not a
    // thing any provider issues.
    const body = name.startsWith('*.') ? name.slice(2) : name;

    if (body.includes('*')) {
        return false;
    }

    if (body.startsWith('.') || body.endsWith('.')) {
        return false;
    }

    // A certificate name needs at least one dot — a bare label is not a
    // resolvable hostname.
    if (!body.includes('.')) {
        return false;
    }

    return true;
};

/**
 * Validate the request form.
 *
 * Returns a map of field key to message; an empty map means valid. Keys for the
 * additional names are `subjectAlternativeNames.<index>` so the form can render
 * the error against the row that caused it.
 */
export const validateCertificateForm = (
    values: CertificateFormValues,
): Record<string, string> => {

    const errors: Record<string, string> = {};

    if (!values.connectionId) {
        errors.connectionId = 'Choose the connection to issue this certificate through';
    }

    if (!isPlausibleHostname(values.domainName)) {
        errors.domainName = 'Enter a hostname, for example example.com or *.example.com';
    }

    // Each additional name is checked, and duplicates are caught — a
    // certificate listing the same name twice is a request the provider will
    // either reject or silently deduplicate, and neither is what the operator
    // meant.
    const seen = new Set<string>([values.domainName.trim()
                                        .toLowerCase()]);

    values.subjectAlternativeNames.forEach((name, index) => {
        const trimmed = name.trim();
        // A blank row is dropped at submit rather than flagged: an operator who
        // added a box and did not fill it meant to add nothing.
        if (trimmed === '') {
            return;
        }
        if (!isPlausibleHostname(trimmed)) {
            errors[`subjectAlternativeNames.${index}`] =
                'Enter a hostname, for example www.example.com';
            return;
        }
        const key = trimmed.toLowerCase();
        if (seen.has(key)) {
            errors[`subjectAlternativeNames.${index}`] =
                'This name is already on the certificate';
            return;
        }
        seen.add(key);
    });

    // ⚠️ THE REGION RULE, AND IT BRANCHES ENTIRELY ON THE TARGET.
    //
    // A CLOUDFRONT certificate is issued in us-east-1 whatever the form says,
    // and the backend REFUSES a contradicting region rather than silently
    // overriding it — deliberately, so a caller who believes they chose
    // eu-west-1 is told they did not. The form therefore does not offer a
    // region for CloudFront at all, and there is nothing to validate.
    //
    // For every other target the region is REQUIRED, because the certificate
    // must live in the same region as the resource it serves and Flexi has no
    // way to guess which that is.
    if (values.targetType !== 'CLOUDFRONT' && values.region.trim() === '') {
        errors.region = 'Choose the region this certificate will be used in';
    }

    return errors;
};

/**
 * Turn validated form values into the mutation input.
 *
 * ⚠️ `region` IS OMITTED ENTIRELY FOR A CLOUDFRONT TARGET rather than sent as
 * us-east-1. The backend applies CloudFront's rule itself; sending a region it
 * is about to ignore invites the reader to believe the client chose it, and
 * would fail outright if the form ever carried a stale value from a target the
 * operator switched away from.
 */
export const toRequestInput = (
    values: CertificateFormValues,
): RequestCertificateInput => {

    const additional = values.subjectAlternativeNames
                             .map(name => name.trim())
                             .filter(name => name !== '');

    return {
        connectionId: values.connectionId,
        domainName: values.domainName.trim(),
        ...(additional.length > 0 ? {subjectAlternativeNames: additional} : {}),
        targetType: values.targetType,
        ...(values.targetType === 'CLOUDFRONT' ? {} : {region: values.region.trim()}),
    };
};
